import React, { useEffect, useState } from 'react'

import { Button, ButtonVariant } from '@amzn/stencil-react-components/button'
import { Col } from '@amzn/stencil-react-components/layout'
import { Modal } from '@amzn/stencil-react-components/modal'
import { Spinner } from '@amzn/stencil-react-components/spinner'
import { Text } from '@amzn/stencil-react-components/text'

import { ModalContent } from 'src/components/ModalContent'
import { BucketsAndCupsGroupDTO } from 'src/models/dto/activities/BucketsAndCupsGroupDTO'
import {
    BucketsAndCupsVisualizer,
    PageItemSummary,
} from 'src/pages/module-builder/buckets-and-cups-editor/BucketsAndCupsVisualizer'
import { loadPoolPageResolver } from 'src/pages/module-builder/buckets-and-cups-editor/ItemContainerPageResolver'
import { BucketsAndCupsSelectionPlanService } from 'src/services/bucketsAndCups/BucketsAndCupsSelectionPlanService'

type PageResolver = (pageLabel: string) => PageItemSummary[]

export interface VisualizeBucketsAndCupsButtonProps {
    /** The Buckets & Cups group being edited. */
    group: BucketsAndCupsGroupDTO
    /** Disables opening the visualizer (e.g. when there is nothing to show). */
    disabled?: boolean
}

/**
 * A "Visualize" button that opens the {@link BucketsAndCupsVisualizer} in a
 * modal for the current group. Mirrors the standalone visualizer page wiring:
 * it resolves the pool's pages and drives selection via the backend simulate
 * API. The page resolver is loaded lazily the first time the modal is opened.
 */
export const VisualizeBucketsAndCupsButton = ({
    group,
    disabled,
}: VisualizeBucketsAndCupsButtonProps) => {
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [resolver, setResolver] = useState<PageResolver | undefined>()

    const poolVersionId = group.poolVersionId

    useEffect(() => {
        if (!isOpen || !poolVersionId) {
            return
        }
        let cancelled = false
        setLoading(true)
        setResolver(undefined)
        loadPoolPageResolver(poolVersionId)
            .then((r) => {
                if (!cancelled) {
                    setResolver(() => r)
                }
            })
            .catch(() => {
                /* best-effort: pages still render with just their label */
            })
            .finally(() => {
                if (!cancelled) {
                    setLoading(false)
                }
            })
        return () => {
            cancelled = true
        }
    }, [isOpen, poolVersionId])

    return (
        <>
            <Modal
                isOpen={isOpen}
                close={() => setIsOpen(false)}
                isScrollable={false}
                dataTestId='buckets-and-cups-visualizer-modal'
            >
                <ModalContent titleText='Buckets & Cups Visualizer' maxWidth='90vw'>
                    <div style={{ minWidth: '70vw', minHeight: '50vh' }}>
                        {loading ? (
                            <Col alignItems='center' gridGap='S200' padding='S400'>
                                <Spinner />
                                <Text fontSize='T100' color='neutral70'>
                                    Loading item containers…
                                </Text>
                            </Col>
                        ) : (
                            <BucketsAndCupsVisualizer
                                group={group}
                                resolvePageItems={resolver}
                                generateSelectionPlan={() =>
                                    BucketsAndCupsSelectionPlanService.generateSelectionPlan(group)
                                }
                            />
                        )}
                    </div>
                </ModalContent>
            </Modal>
            <Button
                dataTestId='open-buckets-and-cups-visualizer'
                aria-label='Visualize buckets and cups'
                variant={ButtonVariant.Secondary}
                disabled={disabled}
                aria-disabled={disabled}
                onClick={() => setIsOpen(true)}
            >
                Visualize
            </Button>
        </>
    )
}
